import { add, scale, viewDir } from "../../math.ts";
import { Btn, type InputCmd } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyImpulse, applyStatus, coneConstructs, coneTargets, damageBarrier, explode, isEnemy, playersInRadius, spawnZone } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, charge, commonInput, consume, contactEnemies, launchProjectile, ownerAlive, ready, secs, simpleProjectile, toGround, trigger } from "../kitlib.ts";

export const V = { force: 0, awakened: 1, thrown: 2, ultDrop: 3 } as const;
export const PK = { Hammer: pk(HeroId.Thud, 0), Spark: pk(HeroId.Thud, 1) } as const;
export const ZK = { Realm: zk(HeroId.Thud, 0) } as const;
export const FX = { Hammer: fxk(HeroId.Thud, 0), Thunder: fxk(HeroId.Thud, 1), Realm: fxk(HeroId.Thud, 2) } as const;

const def: HeroDef = {
  id: HeroId.Thud,
  slug: "thud",
  name: "Thud",
  role: Role.Tank,
  color: "#6aa7ff",
  blurb: "Hammer vanguard who spends and rebuilds thunder charges to dash, awaken, and slam.",
  health: 325,
  armor: 0,
  shields: 300,
  radius: 0.55,
  height: 2.2,
  headRadius: 0.3,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Mug-Masher", key: "LMB", description: "Swing the hammer for 45 damage and restore one thunder charge on hit.", cooldown: 0 },
    [Slot.Secondary]: { name: "Hammer Out", key: "RMB", description: "Throw the hammer for 50 damage, bonus health, and thunder on hit.", cooldown: 6 },
    [Slot.Ability1]: { name: "Storm Shove", key: "Shift", description: "Spend thunder to dash forward, knocking enemies back.", cooldown: 1.5 },
    [Slot.Ability2]: { name: "Static Circle", key: "E", description: "Call a lightning realm that slows enemies and refills thunder.", cooldown: 12 },
    [Slot.Ability3]: { name: "Wake the Mug", key: "F", description: "Spend thunder to awaken, gaining bonus health and lightning hammer swings.", cooldown: 1.5 },
    [Slot.Ultimate]: { name: "Big Weather", key: "Q", description: "Rise, then crash down with a stunning 220 damage thunderburst.", cooldown: 0 },
  },
  passive: { name: "Thunder Tab", description: "Hammer hits restore up to three thunder charges; spending a charge grants bonus health." },
};

function addForce(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, n = 1) {
  setHv(sim.c, eid, V.force, Math.min(3, hv(sim.c, eid, V.force) + n));
}

function spendForce(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, n = 1) {
  const f = hv(sim.c, eid, V.force);
  if (f < n) return false;
  setHv(sim.c, eid, V.force, f - n);
  addOverhealth(sim, eid, n >= 2 ? 150 : 50, secs(4), eid);
  return true;
}

function hammerMelee(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, input: InputCmd, awakened: boolean) {
  let hit = false;
  for (const t of coneTargets(sim, eid, 4, 0.75, (e) => isEnemy(sim, eid, e))) {
    hit = true;
    applyDamage(sim, t, awakened ? 70 : 45, eid, { pos: add(sim.posOf(t), { x: 0, y: 1.1, z: 0 }) });
    applyImpulse(sim, t, scale(viewDir(input.yaw, 0), awakened ? 4 : 2));
  }
  for (const { eid: t } of coneConstructs(sim, eid, 4, 0.75)) {
    hit = true;
    damageConstruct(sim, t, awakened ? 70 : 45, eid);
  }
  if (hit) addForce(sim, eid, 1);
  if (awakened) launchProjectile(sim, eid, PK.Spark, 60, input, { radius: 0.22, seconds: 0.45, dir: viewDir(input.yaw, 0) });
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.force, 3);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.awakened) > 0) setHv(c, eid, V.awakened, hv(c, eid, V.awakened) - 1);
      if (hv(c, eid, V.thrown) > 0) setHv(c, eid, V.thrown, hv(c, eid, V.thrown) - 1);
      if (hv(c, eid, V.ultDrop) > 0) {
        setHv(c, eid, V.ultDrop, hv(c, eid, V.ultDrop) - 1);
        if (hv(c, eid, V.ultDrop) === 1) {
          const impact = toGround(sim, sim.posOf(eid));
          explode(sim, eid, impact, { radius: 8, damage: 220, minDamage: 40, knockback: 10, fxKind: FX.Thunder });
          for (const { eid: t } of playersInRadius(sim, impact, 8, (e) => isEnemy(sim, eid, e))) applyStatus(sim, t, "stun", 1, { source: eid });
        }
      }
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if ((pressed & Btn.Ultimate) && c.Ult.charge[eid]! >= 1) {
        c.Ult.charge[eid] = 0;
        c.Ult.active[eid] = secs(1.5);
        setHv(c, eid, V.ultDrop, secs(1.5));
        applyStatus(sim, eid, "unstoppable", 1.5, { source: eid });
        applyStatus(sim, eid, "resist", 1.5, { source: eid });
        c.Vel.y[eid] = 12.9;
        c.Player.grounded[eid] = 0;
        action(sim, eid, ActionKind.Ult, secs(1.5));
      }
      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        spawnZone(sim, eid, ZK.Realm, sim.posOf(eid), 5, 4);
        action(sim, eid, ActionKind.Cast, 24);
      }
      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1) && spendForce(sim, eid, 1)) {
        consume(sim, eid, Slot.Ability1);
        charge(sim, eid, input, { speed: 22, seconds: 0.45 });
        action(sim, eid, ActionKind.Cast, 16);
      }
      if (c.Move.kind[eid] === MoveKind.Charge) {
        contactEnemies(sim, eid, 1.25, (t) => {
          applyDamage(sim, t, 40, eid);
          applyImpulse(sim, t, { ...scale(viewDir(input.yaw, 0), 10), y: 2 });
        });
      }
      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3) && spendForce(sim, eid, 3)) {
        consume(sim, eid, Slot.Ability3);
        setHv(c, eid, V.awakened, secs(5));
        applyStatus(sim, eid, "unstoppable", 0.5, { source: eid });
        action(sim, eid, ActionKind.Cast, 20);
      }
      if ((pressed & Btn.Secondary) && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        addOverhealth(sim, eid, 75, secs(4), eid);
        setHv(c, eid, V.thrown, secs(0.75));
        launchProjectile(sim, eid, PK.Hammer, 60, input, { radius: 0.3, seconds: 0.4 });
        action(sim, eid, ActionKind.AltFire, 18);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = 36;
        action(sim, eid, ActionKind.Fire, 18);
        hammerMelee(sim, eid, input, hv(c, eid, V.awakened) > 0);
      }
    },
    movement(sim, eid, _input, mods) {
      if (hv(sim.c, eid, V.ultDrop) > 0) mods.gravityMul = 0.25;
    },
  },
  projectiles: {
    [PK.Hammer]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) {
          applyDamage(sim, hit.target, 50, owner, { pos: hit.pos });
          addForce(sim, owner, 1);
        } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 50, owner);
        else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 50, owner);
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Hammer });
      },
      { color: "#91c8ff", size: 0.3, trail: true },
    ),
    [PK.Spark]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) applyDamage(sim, hit.target, 15, owner, { crit: hit.head, pos: hit.pos });
        else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 15, owner);
        else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 15, owner);
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Thunder });
      },
      { color: "#cceaff", size: 0.18, trail: true },
    ),
  },
  zones: {
    [ZK.Realm]: {
      visual: { color: "#6aa7ff", dome: true },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        if (sim.c.Zone.age[z] === 1) {
          const n = playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => isEnemy(sim, owner, e), true).length;
          addForce(sim, owner, n);
        }
        for (const { eid: t } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => isEnemy(sim, owner, e), true)) {
          applyStatus(sim, t, "slow", 0.25, { slowPct: 0.2, source: owner });
          if (sim.c.Player.grounded[t]) applyStatus(sim, t, "root", 0.05, { source: owner });
        }
      },
      end(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        for (const { eid: t } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]! + 0.5, (e) => isEnemy(sim, owner, e), true))
          applyStatus(sim, t, "slow", 2, { slowPct: 0.3, source: owner });
      },
    },
  },
  fx: {
    [FX.Hammer]: { color: "#91c8ff" },
    [FX.Thunder]: { color: "#d6f4ff" },
    [FX.Realm]: { color: "#6aa7ff" },
  },
};

export default module;
